import React, { useState, useEffect, createContext, useContext } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Alert, AlertDescription } from "./components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import AdvancedGraphs from "./components/AdvancedGraphs";
import RegulatoryReports from "./components/RegulatoryReports";
import DocumentsAlerts from "./components/DocumentsAlerts";
import ClientRegistration from "./components/ClientRegistration";
import ClientManagement from "./components/ClientManagement";
import ClientSpace from "./components/ClientSpace";
import EnhancedClientManagement from "./components/EnhancedClientManagement";
import PrestationLibrary from "./components/PrestationLibrary";
import QuoteBuilder from "./components/QuoteBuilder";
import CategoryManagement from "./components/CategoryManagement";
import { ChatbotButton } from "./components/ChatbotAcoustique";
import ChatbotPage from "./components/ChatbotPage";
import RegulatoryReferencesManagement from "./components/RegulatoryReferencesManagement";
import ServiceBubblesHome from "./components/ServiceBubblesHome";
import CinematicIntro from "./components/CinematicIntro";
import HomePage from "./components/HomePage";
import FreeRegistration from "./components/FreeRegistration";
import RegisterWithCode from "./components/RegisterWithCode";
import FreeRegistrationPage from "./components/FreeRegistrationPage";
import ExpertiseReport from "./components/ExpertiseReport";
import ReportEditorV2 from "./components/ReportEditorV2";
import AcousticModuleHub from "./components/AcousticModuleHub";
import QuoteWizard from "./components/QuoteWizard";
import TourismHub from "./components/TourismHub";
import ShamanMap from "./components/ShamanMap";
import CoffreFort from "./components/CoffreFort";
import AdminControlCenter from "./components/AdminControlCenter";
import MeditationModule from "./components/MeditationModule";
import ReportsList from "./components/ReportsList";
import { 
  Volume2, 
  Shield, 
  User, 
  Settings, 
  BarChart3, 
  Music, 
  BookOpen, 
  MapPin, 
  Heart, 
  Radio, 
  Disc3, 
  Zap,
  LogOut,
  Lock,
  Smartphone,
  Eye,
  EyeOff,
  Users,
  FileText,
  Building,
  CheckCircle,
  Activity,
  ArrowLeft,
  ExternalLink,
  RefreshCw,
  AlertTriangle,
  Calendar,
  Calculator,
  Bell,
  Clock,
  Battery,
  Wifi,
  Plus,
  MessageCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('tkale_token');
    const userData = localStorage.getItem('tkale_user');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Error parsing user data:', error);
        logout();
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password, totpCode = null) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        totp_code: totpCode
      });

      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('tkale_token', access_token);
      localStorage.setItem('tkale_user', JSON.stringify(userData));
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('tkale_token');
    localStorage.removeItem('tkale_user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Component
const LoginForm = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    totpCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [needsTOTP, setNeedsTOTP] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleBack = () => {
    // Naviguer vers l'accueil avec un état indiquant de sauter l'intro et aller au portail
    navigate('/', { state: { fromLogin: true } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password, formData.totpCode);
      
      if (result.success) {
        toast.success(`Bienvenue ${result.user.username} !`);
      } else {
        if (result.error.includes('TOTP')) {
          setNeedsTOTP(true);
          toast.error("Code d'authentification requis");
        } else {
          toast.error(result.error);
        }
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Back button */}
      <Button
        variant="ghost"
        className="absolute top-4 left-4 text-cyan-300 hover:text-cyan-100 z-10"
        onClick={handleBack}
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Retour
      </Button>

      <div className="absolute inset-0 opacity-30">
        <div className="w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
      </div>
      
      <Card className="w-full max-w-md relative z-10 shadow-2xl border-0 bg-white/95 backdrop-blur" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-teal-600 rounded-xl flex items-center justify-center">
              <Volume2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                T-Kalé
              </CardTitle>
              <p className="text-sm text-slate-600">Platform Professional</p>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                placeholder="votre@email.com"
                required
                className="mt-1"
                data-testid="email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({...prev, password: e.target.value}))}
                  placeholder="••••••••"
                  required
                  className="mt-1 pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {needsTOTP && (
              <div>
                <Label htmlFor="totpCode">Code d'authentification</Label>
                <div className="flex items-center space-x-2 mt-1">
                  <Smartphone className="h-4 w-4 text-slate-500" />
                  <Input
                    id="totpCode"
                    type="text"
                    value={formData.totpCode}
                    onChange={(e) => setFormData(prev => ({...prev, totpCode: e.target.value}))}
                    placeholder="123456"
                    maxLength="6"
                    className="text-center tracking-widest"
                    data-testid="totp-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Saisissez le code de Google Authenticator
                </p>
              </div>
            )}
            
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700"
              data-testid="login-button"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Connexion...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Shield className="h-4 w-4" />
                  <span>Se connecter</span>
                </div>
              )}
            </Button>
          </form>
          
          <div className="mt-6 space-y-4">
            <div className="text-center">
              <p className="text-sm text-slate-600">
                Pas encore inscrit ? 
                <a href="/register" className="ml-2 text-teal-600 hover:text-teal-700 font-medium">
                  Créer un compte
                </a>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Module Icons Helper
const getModuleIcon = (moduleType, className = "h-5 w-5") => {
  const icons = {
    acoustics: <Volume2 className={className} />,
    music_edition: <Music className={className} />,
    music_streaming: <Radio className={className} />,
    literary_works: <BookOpen className={className} />,
    personal_development: <Zap className={className} />,
    tourism: <MapPin className={className} />,
    wellness: <Heart className={className} />,
    music_label: <Disc3 className={className} />
  };
  return icons[moduleType] || <Activity className={className} />;
};

// Advanced Charts Component - REPLACED WITH NEW RECHARTS VERSION
// Use the AdvancedGraphs component imported from ./components/AdvancedGraphs
/*
const AdvancedChartsComponent = ({ soundmeters }) => {
  const [chartType, setChartType] = useState("temporal");
  const [selectedSoundmeter, setSelectedSoundmeter] = useState("");
  const [weighting, setWeighting] = useState("dB_A");
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadChartData = async () => {
    if (!selectedSoundmeter || !chartType) return;

    setLoading(true);
    try {
      let endpoint = "";
      let params = new URLSearchParams();

      switch(chartType) {
        case "temporal":
          endpoint = `/charts/temporal/${selectedSoundmeter}`;
          params.append('weighting_type', weighting);
          params.append('hours', '24');
          break;
        case "spectrum":
          endpoint = `/charts/spectrum/${selectedSoundmeter}`;
          break;
        case "histogram":
          endpoint = `/charts/histogram/${selectedSoundmeter}`;
          params.append('weighting_type', weighting);
          params.append('hours', '24');
          break;
        default:
          break;
      }

      if (endpoint) {
        const response = await axios.get(`${API}${endpoint}?${params}`);
        setChartData(response.data);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement du graphique");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const renderChart = () => {
    if (!chartData) return null;

    // Simple chart representation with CSS bars
    switch(chartType) {
      case "temporal":
        return (
          <div className="space-y-4">
            <div className="bg-white p-4 rounded border">
              <h4 className="font-medium mb-3">Évolution Temporelle - {weighting}</h4>
              <div className="h-64 flex items-end space-x-1">
                {chartData.data?.slice(0, 20).map((point, index) => (
                  <div
                    key={index}
                    className="bg-teal-500 w-4 rounded-t"
                    style={{ height: `${Math.max(10, (point.value / 100) * 100)}%` }}
                    title={`${point.value?.toFixed(1)} dB`}
                  ></div>
                ))}
              </div>
              <div className="mt-2 text-xs text-slate-500">
                Évolution sur 24h - {chartData.data?.length} points
              </div>
            </div>
          </div>
        );

      case "spectrum":
        return (
          <div className="space-y-4">
            <div className="bg-white p-4 rounded border">
              <h4 className="font-medium mb-3">Analyse Spectrale</h4>
              <div className="h-64 flex items-end space-x-2">
                {chartData.data?.map((point, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div
                      className="bg-blue-500 w-8 rounded-t"
                      style={{ height: `${Math.max(10, (point.level / 80) * 100)}%` }}
                      title={`${point.frequency} Hz: ${point.level?.toFixed(1)} dB`}
                    ></div>
                    <div className="text-xs text-slate-500 mt-1 transform -rotate-45">
                      {point.frequency}Hz
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case "histogram":
        return (
          <div className="space-y-4">
            <div className="bg-white p-4 rounded border">
              <h4 className="font-medium mb-3">Distribution des Niveaux</h4>
              <div className="h-64 flex items-end space-x-1">
                {chartData.data?.map((bin, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div
                      className="bg-emerald-500 w-8 rounded-t"
                      style={{ height: `${Math.max(10, (bin.count / Math.max(...chartData.data.map(d => d.count))) * 100)}%` }}
                      title={`${bin.range}: ${bin.count} occurrences`}
                    ></div>
                    <div className="text-xs text-slate-500 mt-1 transform -rotate-45">
                      {bin.range}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return <div className="text-center py-8 text-slate-500">Sélectionnez un type de graphique</div>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <BarChart3 className="h-5 w-5" />
          <span>Analyses Graphiques Avancées</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label>Sonomètre</Label>
            <Select value={selectedSoundmeter} onValueChange={setSelectedSoundmeter}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir..." />
              </SelectTrigger>
              <SelectContent>
                {soundmeters.map(sm => (
                  <SelectItem key={sm.soundmeter_id} value={sm.soundmeter_id}>
                    {sm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Type de graphique</Label>
            <Select value={chartType} onValueChange={setChartType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="temporal">Évolution temporelle</SelectItem>
                <SelectItem value="spectrum">Spectre fréquentiel</SelectItem>
                <SelectItem value="histogram">Histogramme</SelectItem>
                <SelectItem value="temporal_diff">Évolution différentielle</SelectItem>
                <SelectItem value="histogram_cumul">Histogramme cumulé</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Pondération</Label>
            <Select value={weighting} onValueChange={setWeighting}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dB_A">dB(A)</SelectItem>
                <SelectItem value="dB_C">dB(C)</SelectItem>
                <SelectItem value="dB_L">dB(L)</SelectItem>
                <SelectItem value="dB_Z">dB(Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end">
            <Button 
              onClick={loadChartData} 
              disabled={loading || !selectedSoundmeter}
              className="w-full bg-teal-600 hover:bg-teal-700"
            >
              {loading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <BarChart3 className="h-4 w-4 mr-2" />
              )}
              Générer
            </Button>
          </div>
        </div>

        <div className="min-h-80">
          {loading ? (
            <div className="flex items-center justify-center h-80">
              <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
            </div>
          ) : (
            renderChart()
          )}
        </div>

        {chartData && (
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="font-medium mb-2">Informations sur le graphique</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Sonomètre:</span>
                <div className="font-semibold">{chartData.soundmeter_id}</div>
              </div>
              <div>
                <span className="text-slate-500">Type:</span>
                <div className="font-semibold">{chartType}</div>
              </div>
              <div>
                <span className="text-slate-500">Pondération:</span>
                <div className="font-semibold">{weighting}</div>
              </div>
              <div>
                <span className="text-slate-500">Points de données:</span>
                <div className="font-semibold">{chartData.data?.length || 0}</div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
*/

// Periods Management Component
const PeriodsManagementComponent = ({ soundmeters, periods, onRefresh }) => {
  const [isAddingPeriod, setIsAddingPeriod] = useState(false);
  const [selectedSoundmeter, setSelectedSoundmeter] = useState("");
  const [periodForm, setPeriodForm] = useState({
    name: "",
    measurement_type: "residual",
    start_time: "22:00",
    end_time: "06:00",
    days_of_week: [0, 1, 2, 3, 4],
    duration_days: 7,
    integration_time: 15,
    is_active: true
  });

  const DAYS_OF_WEEK = {
    0: "Lundi", 1: "Mardi", 2: "Mercredi", 3: "Jeudi", 
    4: "Vendredi", 5: "Samedi", 6: "Dimanche"
  };

  const handleCreatePeriod = async (e) => {
    e.preventDefault();
    if (!selectedSoundmeter) return;

    try {
      const soundmeter = soundmeters.find(sm => sm.soundmeter_id === selectedSoundmeter);
      
      await axios.post(`${API}/periods`, {
        ...periodForm,
        site_name: soundmeter.site_name,
        soundmeter_id: selectedSoundmeter
      });
      
      toast.success("Période créée avec succès");
      setIsAddingPeriod(false);
      setPeriodForm({
        name: "",
        measurement_type: "residual",
        start_time: "22:00",
        end_time: "06:00",
        days_of_week: [0, 1, 2, 3, 4],
        duration_days: 7,
        integration_time: 15,
        is_active: true
      });
      onRefresh();
    } catch (error) {
      toast.error("Erreur lors de la création de la période");
      console.error(error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Gestion des Périodes de Mesure</span>
          </div>
          <Button 
            onClick={() => setIsAddingPeriod(true)}
            className="bg-teal-600 hover:bg-teal-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle Période
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isAddingPeriod && (
          <Card className="mb-6 bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-lg">Créer une Nouvelle Période</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreatePeriod} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Sonomètre</Label>
                    <Select value={selectedSoundmeter} onValueChange={setSelectedSoundmeter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir un sonomètre..." />
                      </SelectTrigger>
                      <SelectContent>
                        {soundmeters.map(sm => (
                          <SelectItem key={sm.soundmeter_id} value={sm.soundmeter_id}>
                            {sm.name} ({sm.site_name})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Nom de la période</Label>
                    <Input
                      value={periodForm.name}
                      onChange={(e) => setPeriodForm(prev => ({...prev, name: e.target.value}))}
                      placeholder="Bruit résiduel nocturne"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Type de mesure</Label>
                    <Select value={periodForm.measurement_type} onValueChange={(value) => 
                      setPeriodForm(prev => ({...prev, measurement_type: value}))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residual">Bruit résiduel</SelectItem>
                        <SelectItem value="ambient">Bruit ambiant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Heure début</Label>
                    <Input
                      type="time"
                      value={periodForm.start_time}
                      onChange={(e) => setPeriodForm(prev => ({...prev, start_time: e.target.value}))}
                      required
                    />
                  </div>

                  <div>
                    <Label>Heure fin</Label>
                    <Input
                      type="time"
                      value={periodForm.end_time}
                      onChange={(e) => setPeriodForm(prev => ({...prev, end_time: e.target.value}))}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Jours de la semaine</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(DAYS_OF_WEEK).map(([dayNum, dayName]) => (
                      <Button
                        key={dayNum}
                        type="button"
                        variant={periodForm.days_of_week.includes(parseInt(dayNum)) ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          const dayNumber = parseInt(dayNum);
                          setPeriodForm(prev => ({
                            ...prev,
                            days_of_week: prev.days_of_week.includes(dayNumber)
                              ? prev.days_of_week.filter(d => d !== dayNumber)
                              : [...prev.days_of_week, dayNumber]
                          }));
                        }}
                      >
                        {dayName.slice(0, 3)}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Durée (jours)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="365"
                      value={periodForm.duration_days}
                      onChange={(e) => setPeriodForm(prev => ({...prev, duration_days: parseInt(e.target.value)}))}
                      required
                    />
                  </div>
                  <div>
                    <Label>Temps d'intégration (min)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="60"
                      value={periodForm.integration_time}
                      onChange={(e) => setPeriodForm(prev => ({...prev, integration_time: parseInt(e.target.value)}))}
                      required
                    />
                  </div>
                </div>

                <div className="flex space-x-3">
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                    Créer la période
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsAddingPeriod(false)}>
                    Annuler
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {soundmeters.map((soundmeter) => (
            <div key={soundmeter.soundmeter_id} className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-medium">{soundmeter.name}</h4>
                  <p className="text-sm text-slate-500">{soundmeter.site_name}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                {periods[soundmeter.soundmeter_id]?.map((period) => (
                  <div key={period.id} className="bg-slate-50 rounded p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm">{period.name}</div>
                        <div className="text-xs text-slate-500">
                          {period.start_time} - {period.end_time}
                          {' • '}
                          {period.days_of_week.map(d => DAYS_OF_WEEK[d]).join(', ')}
                        </div>
                      </div>
                      <Badge 
                        variant={period.measurement_type === 'residual' ? 'default' : 'secondary'}
                        className={period.measurement_type === 'residual' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}
                      >
                        {period.measurement_type === 'residual' ? 'Résiduel' : 'Ambiant'}
                      </Badge>
                    </div>
                  </div>
                )) || (
                  <div className="text-sm text-slate-500 text-center py-4">
                    Aucune période configurée
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Network Configuration Component
const NetworkConfigurationComponent = ({ soundmeters }) => {
  const [selectedSoundmeter, setSelectedSoundmeter] = useState("");
  const [networkConfig, setNetworkConfig] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  const [configForm, setConfigForm] = useState({
    wifi_ssid: "",
    wifi_password: "",
    wifi_security: "WPA2",
    mqtt_broker_host: "mqtt.t-kale.com",
    mqtt_broker_port: 1883,
    mqtt_username: "",
    mqtt_password: "",
    server_host: "api.t-kale.com",
    server_port: 443,
    data_send_frequency: 30
  });

  const loadNetworkConfig = async (soundmeterId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/soundmeters/${soundmeterId}/network-config`);
      setNetworkConfig(response.data);
      setConfigForm({
        wifi_ssid: response.data.wifi_ssid || "",
        wifi_password: "",
        wifi_security: response.data.wifi_security || "WPA2",
        mqtt_broker_host: response.data.mqtt_broker_host || "mqtt.t-kale.com",
        mqtt_broker_port: response.data.mqtt_broker_port || 1883,
        mqtt_username: response.data.mqtt_username || "",
        mqtt_password: "",
        server_host: response.data.server_host || "api.t-kale.com",
        server_port: response.data.server_port || 443,
        data_send_frequency: response.data.data_send_frequency || 30
      });
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error("Erreur lors du chargement de la configuration");
      }
    } finally {
      setLoading(false);
    }
  };

  const saveNetworkConfig = async () => {
    if (!selectedSoundmeter) return;

    setLoading(true);
    try {
      await axios.post(`${API}/soundmeters/${selectedSoundmeter}/network-config`, {
        soundmeter_id: selectedSoundmeter,
        ...configForm
      });
      toast.success("Configuration réseau sauvegardée");
      loadNetworkConfig(selectedSoundmeter);
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    if (!selectedSoundmeter) return;

    setTestingConnection(true);
    try {
      const response = await axios.post(`${API}/soundmeters/${selectedSoundmeter}/test-connection`);
      setConnectionStatus(response.data);
      
      if (response.data.status === "connected") {
        toast.success("Test de connexion réussi");
      } else {
        toast.error("Échec du test de connexion");
      }
    } catch (error) {
      toast.error("Erreur lors du test de connexion");
    } finally {
      setTestingConnection(false);
    }
  };

  useEffect(() => {
    if (selectedSoundmeter) {
      loadNetworkConfig(selectedSoundmeter);
    }
  }, [selectedSoundmeter]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Settings className="h-5 w-5" />
          <span>Configuration Réseau</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <Label>Sélectionner un sonomètre</Label>
            <Select value={selectedSoundmeter} onValueChange={setSelectedSoundmeter}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir un sonomètre..." />
              </SelectTrigger>
              <SelectContent>
                {soundmeters.map(sm => (
                  <SelectItem key={sm.soundmeter_id} value={sm.soundmeter_id}>
                    {sm.name} ({sm.site_name})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedSoundmeter && (
            <>
              {/* WiFi Configuration */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium mb-3 flex items-center space-x-2">
                  <Wifi className="h-4 w-4" />
                  <span>Configuration WiFi</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="wifi_ssid">SSID (Nom du réseau)</Label>
                    <Input
                      id="wifi_ssid"
                      value={configForm.wifi_ssid}
                      onChange={(e) => setConfigForm(prev => ({...prev, wifi_ssid: e.target.value}))}
                      placeholder="MonReseauWiFi"
                    />
                  </div>
                  <div>
                    <Label htmlFor="wifi_password">Mot de passe</Label>
                    <Input
                      id="wifi_password"
                      type="password"
                      value={configForm.wifi_password}
                      onChange={(e) => setConfigForm(prev => ({...prev, wifi_password: e.target.value}))}
                      placeholder="••••••••"
                    />
                  </div>
                  <div>
                    <Label htmlFor="wifi_security">Sécurité</Label>
                    <Select value={configForm.wifi_security} onValueChange={(value) => 
                      setConfigForm(prev => ({...prev, wifi_security: value}))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="WPA2">WPA2</SelectItem>
                        <SelectItem value="WPA3">WPA3</SelectItem>
                        <SelectItem value="WEP">WEP</SelectItem>
                        <SelectItem value="OPEN">Ouvert</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* MQTT Configuration */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium mb-3 flex items-center space-x-2">
                  <Radio className="h-4 w-4" />
                  <span>Configuration MQTT</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="mqtt_broker_host">Serveur MQTT</Label>
                    <Input
                      id="mqtt_broker_host"
                      value={configForm.mqtt_broker_host}
                      onChange={(e) => setConfigForm(prev => ({...prev, mqtt_broker_host: e.target.value}))}
                      placeholder="mqtt.t-kale.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mqtt_broker_port">Port</Label>
                    <Input
                      id="mqtt_broker_port"
                      type="number"
                      value={configForm.mqtt_broker_port}
                      onChange={(e) => setConfigForm(prev => ({...prev, mqtt_broker_port: parseInt(e.target.value)}))}
                      placeholder="1883"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mqtt_username">Utilisateur</Label>
                    <Input
                      id="mqtt_username"
                      value={configForm.mqtt_username}
                      onChange={(e) => setConfigForm(prev => ({...prev, mqtt_username: e.target.value}))}
                      placeholder="username"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mqtt_password">Mot de passe</Label>
                    <Input
                      id="mqtt_password"
                      type="password"
                      value={configForm.mqtt_password}
                      onChange={(e) => setConfigForm(prev => ({...prev, mqtt_password: e.target.value}))}
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-3">
                <Button 
                  onClick={saveNetworkConfig} 
                  disabled={loading}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {loading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Settings className="h-4 w-4 mr-2" />
                  )}
                  Sauvegarder
                </Button>

                <Button 
                  onClick={testConnection} 
                  disabled={testingConnection}
                  variant="outline"
                >
                  {testingConnection ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Activity className="h-4 w-4 mr-2" />
                  )}
                  Test Connexion
                </Button>
              </div>

              {/* Connection Status */}
              {connectionStatus && (
                <Alert className={connectionStatus.status === 'connected' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
                  {connectionStatus.status === 'connected' ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                  )}
                  <AlertDescription>
                    <div className="space-y-1">
                      <div>État: <strong>{connectionStatus.status === 'connected' ? 'Connecté' : 'Déconnecté'}</strong></div>
                      {connectionStatus.details && (
                        <div className="text-sm">
                          WiFi: {connectionStatus.details.wifi} | 
                          MQTT: {connectionStatus.details.mqtt} | 
                          Serveur: {connectionStatus.details.server}
                          {connectionStatus.details.latency_ms && ` | Latence: ${connectionStatus.details.latency_ms}ms`}
                        </div>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Emergence Analysis Component
const EmergenceAnalysisComponent = ({ soundmeters }) => {
  const [emergenceData, setEmergenceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSoundmeter, setSelectedSoundmeter] = useState("");
  const [selectedWeighting, setSelectedWeighting] = useState("dB_A");
  const [daysBack, setDaysBack] = useState(7);

  const calculateEmergence = async () => {
    if (!selectedSoundmeter) {
      toast.error("Sélectionnez un sonomètre");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/analysis/emergence/${selectedSoundmeter}?weighting_type=${selectedWeighting}&days_back=${daysBack}`
      );
      setEmergenceData(response.data);
      toast.success("Calcul d'émergence terminé");
    } catch (error) {
      toast.error("Données insuffisantes pour le calcul d'émergence");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Calculator className="h-5 w-5" />
          <span>Calcul d'Émergence</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="soundmeter-select">Sonomètre</Label>
            <Select value={selectedSoundmeter} onValueChange={setSelectedSoundmeter}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir un sonomètre" />
              </SelectTrigger>
              <SelectContent>
                {soundmeters.map(sm => (
                  <SelectItem key={sm.soundmeter_id} value={sm.soundmeter_id}>
                    {sm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="weighting-select">Pondération</Label>
            <Select value={selectedWeighting} onValueChange={setSelectedWeighting}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dB_A">dB(A)</SelectItem>
                <SelectItem value="dB_C">dB(C)</SelectItem>
                <SelectItem value="dB_L">dB(L)</SelectItem>
                <SelectItem value="dB_Z">dB(Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="days-back">Période (jours)</Label>
            <Input
              id="days-back"
              type="number"
              min="1"
              max="30"
              value={daysBack}
              onChange={(e) => setDaysBack(parseInt(e.target.value))}
            />
          </div>
        </div>

        <Button 
          onClick={calculateEmergence} 
          disabled={loading || !selectedSoundmeter}
          className="w-full bg-teal-600 hover:bg-teal-700"
        >
          {loading ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Calculator className="h-4 w-4 mr-2" />
          )}
          Calculer l'Émergence
        </Button>

        {emergenceData && (
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-slate-800">Résultats du Calcul</h4>
            
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-white rounded p-3">
                <div className="text-sm text-slate-500">Bruit Ambiant</div>
                <div className="text-lg font-bold text-orange-600">
                  {emergenceData.ambient_leq?.toFixed(1)} dB
                </div>
              </div>
              <div className="bg-white rounded p-3">
                <div className="text-sm text-slate-500">Bruit Résiduel</div>
                <div className="text-lg font-bold text-blue-600">
                  {emergenceData.residual_leq?.toFixed(1)} dB
                </div>
              </div>
              <div className="bg-white rounded p-3">
                <div className="text-sm text-slate-500">Émergence</div>
                <div className={`text-lg font-bold ${
                  emergenceData.emergence_value > 5 ? 'text-red-600' : 
                  emergenceData.emergence_value > 3 ? 'text-amber-600' : 'text-emerald-600'
                }`}>
                  {emergenceData.emergence_value?.toFixed(1)} dB
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <h5 className="font-medium text-slate-700 mb-2">Bruit Ambiant - Fractiles</h5>
                <div className="space-y-1 text-slate-600">
                  {emergenceData.ambient_l10 && <div>L10: {emergenceData.ambient_l10?.toFixed(1)} dB</div>}
                  {emergenceData.ambient_l50 && <div>L50: {emergenceData.ambient_l50?.toFixed(1)} dB</div>}
                  {emergenceData.ambient_l90 && <div>L90: {emergenceData.ambient_l90?.toFixed(1)} dB</div>}
                </div>
              </div>
              <div>
                <h5 className="font-medium text-slate-700 mb-2">Bruit Résiduel - Fractiles</h5>
                <div className="space-y-1 text-slate-600">
                  {emergenceData.residual_l10 && <div>L10: {emergenceData.residual_l10?.toFixed(1)} dB</div>}
                  {emergenceData.residual_l50 && <div>L50: {emergenceData.residual_l50?.toFixed(1)} dB</div>}
                  {emergenceData.residual_l90 && <div>L90: {emergenceData.residual_l90?.toFixed(1)} dB</div>}
                </div>
              </div>
            </div>

            <div className="text-xs text-slate-500 pt-2 border-t border-slate-200">
              <div>Échantillons ambiant: {emergenceData.sample_count_ambient}</div>
              <div>Échantillons résiduel: {emergenceData.sample_count_residual}</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [clients, setClients] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      if (!silent) setLoading(true);
      
      const dashboardResponse = await axios.get(`${API}/admin/dashboard`);
      setDashboardData(dashboardResponse.data);
      
      const clientsResponse = await axios.get(`${API}/admin/clients`);
      setClients(clientsResponse.data);
      
      const modulesResponse = await axios.get(`${API}/modules/available`);
      setModules(modulesResponse.data);
      
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-teal-600 rounded-lg flex items-center justify-center">
                <Volume2 className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">T-Kalé Platform</h1>
                <p className="text-sm text-slate-500">Administration</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <User className="h-3 w-3 mr-1" />
                {user.username}
              </Badge>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={logout}
                className="text-slate-600 hover:text-slate-800"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card data-testid="total-clients-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Clients totaux</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {dashboardData?.platform_stats?.total_clients || 0}
                    </p>
                  </div>
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="active-subscriptions-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Abonnements actifs</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {dashboardData?.platform_stats?.active_subscriptions || 0}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="soundmeters-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Sonomètres</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {dashboardData?.platform_stats?.total_soundmeters || 0}
                    </p>
                  </div>
                  <Volume2 className="h-8 w-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="modules-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Modules disponibles</p>
                    <p className="text-3xl font-bold text-slate-900">{modules.length}</p>
                  </div>
                  <Settings className="h-8 w-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Modules Grid */}
          <Card>
            <CardHeader>
              <CardTitle>Modules de la Plateforme</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {modules.map((module) => (
                  <div key={module.module_type} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          {getModuleIcon(module.module_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-slate-900 text-sm">{module.name}</h4>
                          <p className="text-xs text-slate-500 mt-1">{module.description}</p>
                          <div className="flex items-center justify-between mt-2">
                            <Badge variant="secondary" className="text-xs">
                              {module.base_price}€/mois
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              v{module.version || '1.0.0'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      <div className="pt-2 border-t border-slate-100">
                        {module.module_type === 'acoustics' ? (
                          <Button 
                            size="sm" 
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            onClick={() => window.location.href = '/acoustics'}
                          >
                            <ExternalLink className="h-3 w-3 mr-2" />
                            Accéder au module
                          </Button>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="w-full"
                            onClick={() => {
                              const moduleRoutes = {
                                'music_edition': '/music-edition',
                                'music_streaming': '/music-streaming', 
                                'literary_works': '/literary-works',
                                'personal_development': '/personal-development',
                                'tourism': '/tourism',
                                'wellness': '/wellness',
                                'music_label': '/music-label'
                              };
                              window.location.href = moduleRoutes[module.module_type] || '/';
                            }}
                          >
                            <ExternalLink className="h-3 w-3 mr-2" />
                            Accéder au module
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Clients Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Gestion des Clients</span>
                <Button 
                  size="sm" 
                  onClick={() => window.location.href = '/admin/clients'}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Gestion Complète
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {clients.slice(0, 3).map((client) => (
                  <div key={client.user_id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-teal-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-medium text-sm">
                          {client.company_name ? client.company_name.charAt(0) : client.username.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">{client.company_name || client.username}</h4>
                        <p className="text-sm text-slate-500">{client.email}</p>
                        <p className="text-xs text-slate-400">
                          Client ID: {client.client_id?.substring(0, 8)}...
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Badge variant={client.is_active ? "default" : "secondary"}>
                        {client.is_active ? "Actif" : "Inactif"}
                      </Badge>
                      
                      <Button size="sm" variant="outline">
                        <Settings className="h-4 w-4 mr-1" />
                        Configurer
                      </Button>
                    </div>
                  </div>
                ))}
                
                {clients.length > 3 && (
                  <div className="text-center pt-2">
                    <Button 
                      variant="link" 
                      size="sm"
                      onClick={() => window.location.href = '/admin/clients'}
                      className="text-teal-600"
                    >
                      Voir tous les clients ({clients.length})
                    </Button>
                  </div>
                )}
                
                {clients.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>Aucun client enregistré</p>
                    <p className="text-sm mt-2">Les nouveaux clients peuvent s'inscrire sur <span className="font-mono text-teal-600">/register</span></p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Prestations Library */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Bibliothèque de Prestations</span>
                <div className="flex space-x-2">
                  <Button 
                    size="sm" 
                    onClick={() => window.location.href = '/admin/categories'}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Gérer Catégories
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => window.location.href = '/admin/prestations'}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Gérer Prestations
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-4 border border-slate-200 rounded-lg bg-gradient-to-r from-purple-50 to-blue-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-slate-900">Prestations réutilisables</h4>
                      <p className="text-sm text-slate-600 mt-1">Créez des modèles de prestations pour vos devis</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-purple-600">4</p>
                      <p className="text-xs text-slate-500">catégories</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    <div className="text-center p-2 bg-white rounded border border-blue-200">
                      <p className="text-xs font-medium text-blue-700">Déplacements</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border border-green-200">
                      <p className="text-xs font-medium text-green-700">Main d'œuvre</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border border-purple-200">
                      <p className="text-xs font-medium text-purple-700">Matériel</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border border-gray-200">
                      <p className="text-xs font-medium text-gray-700">Divers</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quote Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Gestion des Devis</span>
                <Button 
                  size="sm" 
                  onClick={() => window.location.href = '/admin/quotes/create'}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Créer un Devis
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-4 border border-slate-200 rounded-lg bg-gradient-to-r from-blue-50 to-teal-50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-slate-900">Système de devis professionnel</h4>
                      <p className="text-sm text-slate-600 mt-1">Créez des devis détaillés avec calculs automatiques</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 bg-white rounded-lg border border-blue-200">
                      <p className="text-lg font-bold text-blue-600">✓</p>
                      <p className="text-xs text-slate-600 mt-1">Multi-sections</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border border-green-200">
                      <p className="text-lg font-bold text-green-600">∑</p>
                      <p className="text-xs text-slate-600 mt-1">Calculs auto</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border border-purple-200">
                      <p className="text-lg font-bold text-purple-600">📄</p>
                      <p className="text-xs text-slate-600 mt-1">PDF pro</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Chatbot Acoustique */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Assistant Acoustique & Réglementation</span>
                <div className="flex space-x-2">
                  <Button 
                    size="sm" 
                    onClick={() => window.location.href = '/admin/references'}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Gérer Références
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => window.location.href = '/chatbot'}
                    className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700"
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Ouvrir Chatbot
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-4 border border-slate-200 rounded-lg bg-gradient-to-r from-teal-50 to-blue-50">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-teal-600 to-blue-600 rounded-full flex items-center justify-center">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">Expert en réglementation acoustique</h4>
                      <p className="text-sm text-slate-600">Propulsé par GPT-4o</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm text-slate-600">
                    <p>• <strong>Chatbot GPT-4o:</strong> Expert en acoustique française</p>
                    <p>• <strong>7 Références pré-chargées:</strong> NF S31-010, R1334-33, etc.</p>
                    <p>• Normes, lois, protocoles et explications</p>
                    <p>• Support pour vos devis et mesures</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Client Dashboard
const ClientDashboard = () => {
  const { user } = useAuth();
  
  // Redirect to new client space
  useEffect(() => {
    window.location.href = '/my-space';
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-teal-600 rounded-lg flex items-center justify-center">
                <Volume2 className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">T-Kalé Platform</h1>
                <p className="text-sm text-slate-500">{dashboardData?.client_info?.company_name}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                <Building className="h-3 w-3 mr-1" />
                Client
              </Badge>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={logout}
                className="text-slate-600 hover:text-slate-800"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          
          {/* Welcome Section */}
          <Card className="bg-gradient-to-r from-blue-600 to-teal-600 text-white border-0">
            <CardContent className="p-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2">
                    Bienvenue {user.username} !
                  </h2>
                  <p className="text-blue-100 mb-4">
                    Accédez à vos modules et rapports personnalisés
                  </p>
                  <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                    Statut: {dashboardData?.client_info?.subscription_status || 'N/A'}
                  </Badge>
                </div>
                <div className="text-6xl opacity-20">
                  <Volume2 />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Available Modules */}
          <Card>
            <CardHeader>
              <CardTitle>Vos Modules Actifs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dashboardData?.available_modules?.map((moduleType) => (
                  <div key={moduleType} className="p-6 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        {getModuleIcon(moduleType, "h-6 w-6")}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">
                          {moduleType === 'acoustics' && 'T-Kalé Pro-Acoustics'}
                          {moduleType === 'music_edition' && 'Édition Musicale'}
                          {moduleType === 'music_streaming' && 'Streaming Musical'}
                          {moduleType === 'literary_works' && 'Œuvres Littéraires'}
                          {moduleType === 'personal_development' && 'Développement Personnel'}
                          {moduleType === 'tourism' && 'Parcours Touristiques'}
                          {moduleType === 'wellness' && 'Bien-Être'}
                          {moduleType === 'music_label' && 'Label Musical'}
                        </h3>
                        <Badge variant="outline" className="text-xs mt-1">
                          Accès autorisé
                        </Badge>
                      </div>
                    </div>
                    
                    {moduleType === 'acoustics' && (
                      <div className="space-y-2">
                        <p className="text-sm text-slate-600">
                          {dashboardData?.soundmeters?.length || 0} sonomètre(s) configuré(s)
                        </p>
                        <Button size="sm" className="w-full">
                          Accéder au module
                        </Button>
                      </div>
                    )}
                    
                    {moduleType !== 'acoustics' && (
                      <div className="space-y-2">
                        <p className="text-sm text-slate-600">
                          Module disponible - Fonctionnalités complètes
                        </p>
                        <Button size="sm" variant="outline" className="w-full">
                          Bientôt disponible
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {(!dashboardData?.available_modules || dashboardData.available_modules.length === 0) && (
                <div className="text-center py-12 text-slate-500">
                  <Activity className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">
                    Aucun module activé
                  </h3>
                  <p>Contactez votre administrateur pour activer des modules</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Reports */}
          <Card>
            <CardHeader>
              <CardTitle>Rapports Récents</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData?.recent_reports?.length > 0 ? (
                <div className="space-y-3">
                  {dashboardData.recent_reports.map((report) => (
                    <div key={report.report_id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-slate-400" />
                        <div>
                          <h4 className="font-medium text-slate-900">{report.title}</h4>
                          <p className="text-sm text-slate-500">
                            {new Date(report.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline">
                        Consulter
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p>Aucun rapport disponible</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// T-Kalé Pro-Acoustics Module Complet
const AcousticsModule = () => {
  const { user, logout } = useAuth();
  const [soundmeters, setSoundmeters] = useState([]);
  const [measurements, setMeasurements] = useState({});
  const [thresholds, setThresholds] = useState({});
  const [periods, setPeriods] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Constants for the acoustics module
  const MEASUREMENT_TYPES = {
    continuous: "Mesure continue",
    ambient: "Bruit ambiant", 
    residual: "Bruit résiduel"
  };

  const WEIGHTING_TYPES = {
    dB_A: "dB(A)",
    dB_C: "dB(C)",
    dB_L: "dB(L)",
    dB_Z: "dB(Z)"
  };

  const DAYS_OF_WEEK = {
    0: "Lundi", 1: "Mardi", 2: "Mercredi", 3: "Jeudi", 
    4: "Vendredi", 5: "Samedi", 6: "Dimanche"
  };

  useEffect(() => {
    fetchAcousticsData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => fetchAcousticsData(true), 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAcousticsData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      
      // Fetch soundmeters
      const soundmetersResponse = await axios.get(`${API}/soundmeters`);
      setSoundmeters(soundmetersResponse.data || []);

      // Fetch latest measurements and related data for each soundmeter
      const measurementsData = {};
      const thresholdsData = {};
      const periodsData = {};
      
      if (soundmetersResponse.data) {
        await Promise.all(
          soundmetersResponse.data.map(async (sm) => {
            try {
              // Latest measurement
              const measurementResponse = await axios.get(`${API}/measurements/latest/${sm.soundmeter_id}`);
              measurementsData[sm.soundmeter_id] = measurementResponse.data;
            } catch (error) {
              console.log(`No measurements for ${sm.soundmeter_id}`);
            }

            try {
              // Thresholds
              const thresholdResponse = await axios.get(`${API}/thresholds/${sm.soundmeter_id}`);
              thresholdsData[sm.soundmeter_id] = thresholdResponse.data;
            } catch (error) {
              console.log(`No thresholds for ${sm.soundmeter_id}`);
            }

            try {
              // Periods
              const periodResponse = await axios.get(`${API}/periods/${sm.soundmeter_id}`);
              periodsData[sm.soundmeter_id] = periodResponse.data;
            } catch (error) {
              console.log(`No periods for ${sm.soundmeter_id}`);
            }
          })
        );
      }

      setMeasurements(measurementsData);
      setThresholds(thresholdsData);
      setPeriods(periodsData);
      setLastRefresh(new Date());
      
    } catch (error) {
      toast.error('Erreur lors du chargement des données acoustiques');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Advanced sound level indicator with fractiles
  const AdvancedSoundIndicator = ({ measurement, type = "dB(A)" }) => {
    if (!measurement) return null;

    const getValue = (type) => {
      switch(type) {
        case "dB(A)": return measurement.db_a;
        case "dB(C)": return measurement.db_c;
        case "dB(L)": return measurement.db_l;
        case "LEQ(A)": return measurement.leq_a;
        case "LEQ(C)": return measurement.leq_c;
        default: return measurement.db_a;
      }
    };

    const level = getValue(type);
    if (!level) return null;

    const getColor = (level) => {
      if (level < 50) return "bg-emerald-500";
      if (level < 70) return "bg-amber-500"; 
      return "bg-red-500";
    };

    const getIntensity = (level) => {
      return Math.min(100, Math.max(0, (level - 30) * 2));
    };

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Volume2 className="h-4 w-4 text-slate-600" />
            <span className="text-sm font-medium">{type}</span>
          </div>
          <span className="text-lg font-bold text-slate-800">{level?.toFixed(1)} dB</span>
        </div>
        
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-1000 ${getColor(level)}`}
            style={{ width: `${getIntensity(level)}%` }}
          />
        </div>

        {measurement.l10 && measurement.l50 && measurement.l90 && (
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>L90: {measurement.l90?.toFixed(1)}</span>
            <span>L50: {measurement.l50?.toFixed(1)}</span>
            <span>L10: {measurement.l10?.toFixed(1)}</span>
          </div>
        )}
      </div>
    );
  };

  // Enhanced soundmeter card
  const EnhancedSoundmeterCard = ({ soundmeter, latestMeasurement, thresholds = [] }) => {
    const isOnline = latestMeasurement && 
      new Date() - new Date(latestMeasurement.timestamp) < 300000;

    const formatTime = (timestamp) => {
      return new Date(timestamp).toLocaleString('fr-FR');
    };

    const getMeasurementTypeColor = (type) => {
      switch(type) {
        case 'residual': return 'bg-blue-100 text-blue-800';
        case 'ambient': return 'bg-orange-100 text-orange-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    };

    const checkThresholdAlerts = () => {
      if (!latestMeasurement || !thresholds.length) return [];
      
      return thresholds.filter(threshold => {
        if (!threshold.is_active) return false;
        
        let currentValue;
        switch(threshold.weighting_type) {
          case 'dB_A': currentValue = latestMeasurement.db_a; break;
          case 'dB_C': currentValue = latestMeasurement.db_c; break;
          case 'dB_L': currentValue = latestMeasurement.db_l; break;
          default: currentValue = latestMeasurement.db_a;
        }
        
        return currentValue && currentValue > threshold.threshold_value;
      });
    };

    const alerts = checkThresholdAlerts();

    return (
      <Card className={`relative overflow-hidden border-l-4 ${
        alerts.length > 0 ? 'border-l-red-500' : 
        latestMeasurement?.measurement_type === 'residual' ? 'border-l-blue-500' :
        latestMeasurement?.measurement_type === 'ambient' ? 'border-l-orange-500' :
        'border-l-teal-500'
      }`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-slate-800">
                {soundmeter.name}
              </CardTitle>
              <div className="flex items-center space-x-2 text-sm text-slate-600 mt-1">
                <MapPin className="h-4 w-4" />
                <span>{typeof soundmeter.location === "string" ? soundmeter.location : soundmeter.site_name}</span>
              </div>
              {soundmeter.geolocation && (
                <div className="text-xs text-slate-500 mt-1">
                  📍 {soundmeter.geolocation.lat?.toFixed(4)}, {soundmeter.geolocation.lon?.toFixed(4)}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end space-y-2">
              <Badge 
                variant={isOnline ? "default" : "destructive"}
                className={isOnline ? "bg-emerald-100 text-emerald-800" : ""}
              >
                {isOnline ? <CheckCircle className="h-3 w-3 mr-1" /> : <AlertTriangle className="h-3 w-3 mr-1" />}
                {isOnline ? "En ligne" : "Hors ligne"}
              </Badge>
              
              {latestMeasurement && (
                <Badge className={getMeasurementTypeColor(latestMeasurement.measurement_type)}>
                  {MEASUREMENT_TYPES[latestMeasurement.measurement_type]}
                </Badge>
              )}
              
              {alerts.length > 0 && (
                <Badge variant="destructive">
                  <Bell className="h-3 w-3 mr-1" />
                  {alerts.length} Alerte(s)
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {latestMeasurement ? (
            <>
              <div className="grid grid-cols-1 gap-4">
                <AdvancedSoundIndicator measurement={latestMeasurement} type="dB(A)" />
                <AdvancedSoundIndicator measurement={latestMeasurement} type="dB(C)" />
                
                {latestMeasurement.leq_a && (
                  <AdvancedSoundIndicator measurement={latestMeasurement} type="LEQ(A)" />
                )}
              </div>

              {/* Detailed fractile data */}
              {latestMeasurement.l10 && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <h4 className="text-sm font-medium mb-2">Indices Fractiles</h4>
                  <div className="grid grid-cols-5 gap-2 text-xs">
                    {latestMeasurement.l99 && (
                      <div className="text-center">
                        <div className="text-slate-500">L99</div>
                        <div className="font-semibold">{latestMeasurement.l99?.toFixed(1)}</div>
                      </div>
                    )}
                    {latestMeasurement.l95 && (
                      <div className="text-center">
                        <div className="text-slate-500">L95</div>
                        <div className="font-semibold">{latestMeasurement.l95?.toFixed(1)}</div>
                      </div>
                    )}
                    <div className="text-center">
                      <div className="text-slate-500">L90</div>
                      <div className="font-semibold">{latestMeasurement.l90?.toFixed(1)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-slate-500">L50</div>
                      <div className="font-semibold">{latestMeasurement.l50?.toFixed(1)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-slate-500">L10</div>
                      <div className="font-semibold">{latestMeasurement.l10?.toFixed(1)}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Environmental data */}
              {(latestMeasurement.temperature || latestMeasurement.humidity) && (
                <div className="flex justify-between text-xs text-slate-600 bg-slate-50 rounded p-2">
                  {latestMeasurement.temperature && (
                    <span>🌡️ {latestMeasurement.temperature}°C</span>
                  )}
                  {latestMeasurement.humidity && (
                    <span>💧 {latestMeasurement.humidity}%</span>
                  )}
                </div>
              )}

              {/* Min/Max levels */}
              <div className="grid grid-cols-3 gap-3 pt-3 border-t border-slate-100">
                {latestMeasurement.lmin && (
                  <div className="text-center">
                    <div className="text-xs text-slate-500">LMIN</div>
                    <div className="text-sm font-semibold text-emerald-600">{latestMeasurement.lmin?.toFixed(1)} dB</div>
                  </div>
                )}
                {latestMeasurement.leq_a && (
                  <div className="text-center">
                    <div className="text-xs text-slate-500">LEQ</div>
                    <div className="text-sm font-semibold">{latestMeasurement.leq_a?.toFixed(1)} dB</div>
                  </div>
                )}
                {latestMeasurement.lmax && (
                  <div className="text-center">
                    <div className="text-xs text-slate-500">LMAX</div>
                    <div className="text-sm font-semibold text-red-600">{latestMeasurement.lmax?.toFixed(1)} dB</div>
                  </div>
                )}
              </div>

              {/* Active alerts */}
              {alerts.length > 0 && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    Seuil(s) dépassé(s): {alerts.map(a => a.name).join(", ")}
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                <div className="flex items-center space-x-3 text-xs text-slate-500">
                  <div className="flex items-center space-x-1">
                    <Clock className="h-3 w-3" />
                    <span>{latestMeasurement.integration_time}min</span>
                  </div>
                  {latestMeasurement.battery_level && (
                    <div className="flex items-center space-x-1">
                      <Battery className="h-3 w-3" />
                      <span>{Math.round(latestMeasurement.battery_level)}%</span>
                    </div>
                  )}
                  {latestMeasurement.wifi_strength && (
                    <div className="flex items-center space-x-1">
                      <Wifi className="h-3 w-3" />
                      <span>{latestMeasurement.wifi_strength} dBm</span>
                    </div>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {formatTime(latestMeasurement.timestamp)}
                </div>
              </div>
            </>
          ) : (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Aucune mesure disponible pour ce sonomètre
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  // Import required icons
  const Bell = ({ className }) => <Activity className={className} />;
  const Clock = ({ className }) => <Activity className={className} />;
  const Battery = ({ className }) => <Activity className={className} />;
  const Wifi = ({ className }) => <Activity className={className} />;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Chargement de T-Kalé Pro-Acoustics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => window.history.back()}
                className="mr-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour
              </Button>
              
              <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-blue-600 rounded-lg flex items-center justify-center">
                <Volume2 className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">T-Kalé Pro-Acoustics</h1>
                <p className="text-sm text-slate-500">Système professionnel de métrologie acoustique</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <User className="h-3 w-3 mr-1" />
                {user.username}
              </Badge>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={logout}
                className="text-slate-600 hover:text-slate-800"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 md:grid-cols-8 gap-1">
            <TabsTrigger value="dashboard" className="text-xs md:text-sm">Tableau de bord</TabsTrigger>
            <TabsTrigger value="map" className="text-xs md:text-sm">Carte</TabsTrigger>
            <TabsTrigger value="charts" className="text-xs md:text-sm">Graphiques</TabsTrigger>
            <TabsTrigger value="reports" className="text-xs md:text-sm">Rapports</TabsTrigger>
            <TabsTrigger value="documents" className="text-xs md:text-sm">Documents & Alertes</TabsTrigger>
            <TabsTrigger value="periods" className="text-xs md:text-sm">Périodes</TabsTrigger>
            <TabsTrigger value="network" className="text-xs md:text-sm">Réseau</TabsTrigger>
            <TabsTrigger value="emergence" className="text-xs md:text-sm">Émergence</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {/* Status bar */}
            <div className="bg-white p-4 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <Activity className="h-5 w-5 text-emerald-500" />
                    <span className="text-sm font-medium">
                      {soundmeters.filter(sm => measurements[sm.soundmeter_id]).length} sonomètre(s) actif(s)
                    </span>
                  </div>
                  <div className="text-sm text-slate-500">
                    Dernière mise à jour: {lastRefresh.toLocaleTimeString('fr-FR')}
                  </div>
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={fetchAcousticsData}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
              </div>
            </div>

            {/* Soundmeter cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {soundmeters.map((soundmeter) => (
                <EnhancedSoundmeterCard
                  key={soundmeter.soundmeter_id}
                  soundmeter={soundmeter}
                  latestMeasurement={measurements[soundmeter.soundmeter_id]}
                  thresholds={thresholds[soundmeter.soundmeter_id] || []}
                />
              ))}
            </div>

            {soundmeters.length === 0 && !loading && (
              <div className="text-center py-12">
                <Volume2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  T-Kalé Pro-Acoustics
                </h3>
                <p className="text-slate-500 mb-4">
                  Commencez par configurer votre premier sonomètre pour voir les données.
                </p>
                <Button className="bg-teal-600 hover:bg-teal-700">
                  Configurer un sonomètre
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="map">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5" />
                  <span>Carte des Sonomètres</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-96 bg-slate-100 rounded-lg flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-green-100"></div>
                  
                  {soundmeters.map((sm, index) => {
                    const position = {
                      top: `${30 + index * 25}%`,
                      left: `${20 + index * 30}%`
                    };
                    
                    return (
                      <div
                        key={sm.soundmeter_id}
                        className="absolute cursor-pointer transform -translate-x-1/2 -translate-y-1/2"
                        style={position}
                      >
                        <div className="relative">
                          <div className="w-4 h-4 bg-teal-500 rounded-full border-2 border-white shadow-lg animate-pulse"></div>
                          <div className="absolute -top-8 -left-8 bg-white px-2 py-1 rounded shadow text-xs whitespace-nowrap">
                            {sm.name}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  <div className="absolute bottom-4 left-4 text-xs text-slate-500">
                    Cliquez sur un point pour sélectionner le sonomètre
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="charts">
            <AdvancedGraphs soundmeters={soundmeters} />
          </TabsContent>

          <TabsContent value="reports">
            <RegulatoryReports soundmeters={soundmeters} />
          </TabsContent>

          <TabsContent value="documents">
            <DocumentsAlerts soundmeters={soundmeters} />
          </TabsContent>

          <TabsContent value="periods">
            <PeriodsManagementComponent soundmeters={soundmeters} periods={periods} onRefresh={fetchAcousticsData} />
          </TabsContent>

          <TabsContent value="network">
            <NetworkConfigurationComponent soundmeters={soundmeters} />
          </TabsContent>

          <TabsContent value="emergence">
            <EmergenceAnalysisComponent soundmeters={soundmeters} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Module placeholder pour les autres modules
const ModulePlaceholder = ({ moduleName, moduleIcon }) => {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => window.history.back()}
                className="mr-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour
              </Button>
              
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-teal-600 rounded-lg flex items-center justify-center">
                {moduleIcon}
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">{moduleName}</h1>
                <p className="text-sm text-slate-500">Module en développement</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <User className="h-3 w-3 mr-1" />
                {user.username}
              </Badge>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={logout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            {React.cloneElement(moduleIcon, { className: "h-12 w-12 text-blue-600" })}
          </div>
          
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            {moduleName}
          </h2>
          
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Ce module est actuellement en développement. Il sera bientôt disponible avec toutes ses fonctionnalités.
          </p>
          
          <div className="space-y-4">
            <Badge variant="outline" className="text-base px-4 py-2">
              🚧 En développement
            </Badge>
            
            <div className="mt-8">
              <Button variant="outline" onClick={() => window.history.back()}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour au tableau de bord
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  return (
    <AuthProvider>
      <div className="App">
        <Toaster position="top-right" />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
};

const AppContent = () => {
  const { user, loading, logout } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-teal-600 rounded-xl flex items-center justify-center mb-4 mx-auto">
            <Volume2 className="h-6 w-6 text-white animate-pulse" />
          </div>
          <h2 className="text-xl font-semibold text-slate-700 mb-2">T-Kalé Platform</h2>
          <p className="text-slate-500">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Routes>
          <Route path="/" element={<HomePage key="no-user" user={null} onLogout={logout} />} />
          <Route path="/login" element={<LoginForm />} />
          <Route path="/register" element={<ClientRegistration />} />
          <Route path="/register-free" element={<FreeRegistrationPage />} />
          <Route path="/register-with-code" element={<RegisterWithCode />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <ChatbotButton />
      </>
    );
  }

  return (
    <>
    <Routes>
      {/* Page d'accueil 3D pour tous les utilisateurs avec intro cinématique */}
      <Route path="/" element={<HomePage key={user?.user_id || 'logged-in'} user={user} onLogout={logout} />} />
      
      {/* Centre de Contrôle Admin - Gardien de la Graine */}
      <Route path="/admin/control-center" element={<AdminControlCenter user={user} onLogout={logout} />} />
      
      {/* Tableau de bord administrateur (ancien - redirige vers le Centre de Contrôle) */}
      <Route path="/admin/dashboard" element={<AdminControlCenter user={user} onLogout={logout} />} />
      
      {/* Module Acoustique - Hub avec sous-modules */}
      <Route path="/acoustics" element={<AcousticModuleHub />} />
      <Route path="/acoustics/prog" element={<AcousticsModule />} />
      <Route path="/acoustics/reports" element={<ReportsList />} />
      <Route path="/acoustics/report" element={<ExpertiseReport />} />
      <Route path="/acoustics/report/new" element={<ReportEditorV2 />} />
      <Route path="/acoustics/report/edit/:reportId" element={<ReportEditorV2 />} />
      <Route path="/acoustics/report/:reportId" element={<ExpertiseReport />} />
      
      {/* Other Module Routes */}
      <Route path="/clients" element={<ClientManagement />} />
      <Route path="/admin/clients" element={<EnhancedClientManagement />} />
      <Route path="/admin/prestations" element={<PrestationLibrary />} />
      <Route path="/admin/categories" element={<CategoryManagement />} />
      <Route path="/admin/references" element={<RegulatoryReferencesManagement />} />
      <Route path="/admin/quotes/create" element={<QuoteBuilder />} />
      
      {/* Module Devis */}
      <Route path="/quotes/new" element={<QuoteWizard />} />
      <Route path="/quotes/:quoteId" element={<QuoteWizard />} />
      
      {/* Module Tourisme */}
      <Route path="/tourism" element={<TourismHub />} />
      <Route path="/tourism/shaman-map" element={<ShamanMap />} />
      
      {/* Coffre Fort & Charte */}
      <Route path="/coffre-fort" element={<CoffreFort />} />
      
      {/* Module Méditation */}
      <Route path="/meditation" element={<MeditationModule user={user} />} />
      
      <Route path="/chatbot" element={<ChatbotPage />} />
      <Route path="/my-space" element={<ClientSpace user={user} />} />
      <Route path="/music-edition" element={
        <ModulePlaceholder 
          moduleName="T-Kalé Édition Musicale" 
          moduleIcon={<Music className="h-4 w-4 text-white" />} 
        />
      } />
      <Route path="/music-streaming" element={
        <ModulePlaceholder 
          moduleName="T-Kalé Streaming" 
          moduleIcon={<Radio className="h-4 w-4 text-white" />} 
        />
      } />
      <Route path="/literary-works" element={
        <ModulePlaceholder 
          moduleName="T-Kalé Littéraire" 
          moduleIcon={<BookOpen className="h-4 w-4 text-white" />} 
        />
      } />
      <Route path="/personal-development" element={
        <ModulePlaceholder 
          moduleName="T-Kalé Développement" 
          moduleIcon={<Zap className="h-4 w-4 text-white" />} 
        />
      } />
      <Route path="/wellness" element={
        <ModulePlaceholder 
          moduleName="T-Kalé Bien-Être" 
          moduleIcon={<Heart className="h-4 w-4 text-white" />} 
        />
      } />
      <Route path="/music-label" element={
        <ModulePlaceholder 
          moduleName="T-Kalé Label" 
          moduleIcon={<Disc3 className="h-4 w-4 text-white" />} 
        />
      } />
      
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <ChatbotButton />
  </>
  );
};

export default App;